// Required-document checklist. Items come from `template.workflow.document_checklist`
// (preferred) or `config.items[]` (fallback). Each item is checked when
// `matter.documents` contains a doc whose `type` matches the item id.

import type { MatterDocument } from "../../../data/types";
import type { WidgetProps } from "./types";
import { WidgetCard } from "./WidgetCard";

interface ChecklistItem {
  id: string;
  label: string;
  required?: boolean;
}

function deriveItems(
  config: WidgetProps["config"],
  template: WidgetProps["template"],
): ChecklistItem[] {
  const fromTemplate =
    (template?.workflow?.document_checklist as ChecklistItem[] | undefined) ?? [];
  if (fromTemplate.length > 0) return fromTemplate;
  const ids = (config.items as string[] | undefined) ?? [];
  return ids.map((id) => ({
    id,
    label: id.replace(/_/g, " "),
    required: false,
  }));
}

function hasDoc(documents: MatterDocument[] | undefined, id: string): boolean {
  if (!documents) return false;
  const k = id.toLowerCase();
  return documents.some(
    (d) => (d.type ?? "").toLowerCase() === k || (d.title ?? "").toLowerCase().includes(k),
  );
}

export function DocChecklistWidget({ config, matter, template }: WidgetProps) {
  const items = deriveItems(config, template);
  const checks = items.map((it) => ({ ...it, present: hasDoc(matter.documents, it.id) }));
  const done = checks.filter((c) => c.present).length;

  return (
    <WidgetCard title={config.title ?? "Document checklist"} sub={`${done}/${items.length}`}>
      {items.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "var(--muted)" }}>No checklist configured.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {checks.map((c) => (
            <div
              key={c.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 0",
                fontSize: 12.5,
                color: c.present ? "var(--ink)" : "var(--ink-2)",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  background: c.present
                    ? "var(--ok-50)"
                    : c.required
                      ? "var(--red-50)"
                      : "var(--bg, var(--line-2))",
                  color: c.present
                    ? "var(--ok-600)"
                    : c.required
                      ? "var(--red-600)"
                      : "var(--muted)",
                }}
              >
                {c.present ? "✓" : c.required ? "!" : "·"}
              </span>
              <span style={{ flex: 1, textTransform: "capitalize" }}>{c.label}</span>
              {c.required && !c.present && (
                <span
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "var(--red-600)",
                  }}
                >
                  required
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}
